import { HttpErrorResponse } from '@angular/common/http';
import { getApiErrorMessage, stripSupportReferenceCode } from 'app/shared/screens/deo-ai-assistant/services/api-error.helper';

/**
 * The embed token route answers failures with FastAPI's `{detail}`, not the `{data, message,
 * succeeded}` envelope the rest of mcp-gx-data-client uses — so `getApiErrorMessage` alone finds
 * nothing to show and falls through to its fallback, losing a message that was written for the user.
 *
 * This reads the envelope first (via that helper, unchanged) and the `detail` second, and puts both
 * through the same PLAN-2314 strip, so a support reference code cannot reach the screen by this route
 * either.
 */
export function getDashboardEmbedErrorMessage(error: unknown, fallback: string): string {
  const envelopeMessage = getApiErrorMessage(error, '');
  if (envelopeMessage) {
    return envelopeMessage;
  }

  const detail = readStringDetail(error);
  const cleaned = detail ? stripSupportReferenceCode(detail).trim() : '';

  return cleaned || fallback;
}

/**
 * The backend's message with its support reference code still on it. **For logging only** — the code
 * means nothing to the user, but it is exactly what the backend team needs to find the failure in
 * their logs, so a caller logs this while showing {@link getDashboardEmbedErrorMessage}.
 */
export function getRawDashboardEmbedErrorMessage(error: unknown): string | undefined {
  const body = readErrorBody(error);

  if (body?.succeeded === false && typeof body?.message === 'string') {
    return body.message;
  }

  return readStringDetail(error);
}

/**
 * `detail` is taken **only when it is a string**: FastAPI's own 422 validation errors put an array of
 * objects there, which is for a developer to read in the console and never for a screen.
 */
function readStringDetail(error: unknown): string | undefined {
  const detail = readErrorBody(error)?.detail;

  return typeof detail === 'string' ? detail : undefined;
}

function readErrorBody(error: unknown): { succeeded?: boolean; message?: unknown; detail?: unknown } | undefined {
  return error instanceof HttpErrorResponse ? error.error : undefined;
}
